import { defineStore } from 'pinia';
import { ref, reactive } from 'vue';
import { faFacebookF, faTwitter, faInstagram } from '@fortawesome/free-brands-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import ls from '@/services/ls';

interface NavPage {
  label: string;
  route: string;
}

interface AppInfo {
  name: string;
  website: string;
  logo: string;
  themeColor: string;
  themeOverlay: string;
  themeBackground: string;
  catchPhrase: string;
  description: string;
}

interface SocialLink {
  platform: string;
  url: string;
  icon: IconDefinition;
}

export const useAppStore = defineStore('app', () => {
  // State
  const mobile = ref(false);
  const theme = ref<'light' | 'dark'>('light');
  const scrollDisabled = ref(false);
  const newsLetterPopupActive = ref(false);
  const navPages = ref<NavPage[]>([]);
  const footerPages = ref<NavPage[]>([]);

  // Apps data
  const apps = reactive<Record<string, AppInfo>>({
    zephyr: {
      name: 'Zephyr',
      website: 'https://zephyr-sim.com/',
      logo: '/assets/images/zephyr-logo-white.png',
      themeColor: '#5d2b13',
      themeOverlay: 'linear-gradient( rgb(93, 43, 19), rgb(82, 33, 65))',
      themeBackground: '/assets/images/zephyr-background.png',
      catchPhrase: 'Take Control. Take Flight',
      description:
        "Specifically designed for use by commercial operators, Zephyr can be used to train operators of all experience and skill levels. Whether you've been flying drones for years or have never picked up a controller before, Zephyr has you covered!",
    },
    ivis: {
      name: 'IVIS',
      website: 'https://ivis.littlearms.com/',
      logo: '/assets/images/ivis_logo_title_white.png',
      themeColor: '#ff5100',
      themeOverlay:
        'linear-gradient(to top, rgb(255, 81, 0), rgba(255, 0, 37, 0.73) 76.05%, rgba(150, 0, 72, 0.86))',
      themeBackground: '/assets/videos/nodes.mp4',
      catchPhrase: 'The New Standard for Fire Training Excellence',
      description:
        'Our mission is to bring more firefighters home safe by making incident training more effective, accessible, and affordable for every fire department. Our Interactive Virtual Incident Simulator uses real-time, data-driven software to train fire officers and firefighters to make smart, swift decisions when lives are on the line.',
    },
  });

  // Social links
  const social = reactive<Record<string, SocialLink>>({
    facebook: {
      platform: 'Facebook',
      url: 'https://www.facebook.com/littlearmsstudios/',
      icon: faFacebookF,
    },
    twitter: {
      platform: 'Twitter',
      url: 'https://twitter.com/LASgames',
      icon: faTwitter,
    },
    instagram: {
      platform: 'Instagram',
      url: 'https://www.instagram.com/little_arms_studios/',
      icon: faInstagram,
    },
  });

  // Actions
  function setMobile(isMobile: boolean) {
    mobile.value = isMobile;
    ls.set('view-mode', isMobile ? 'mobile' : 'desktop');
  }

  function setTheme(newTheme: 'light' | 'dark') {
    theme.value = newTheme === 'dark' ? 'dark' : 'light';
    ls.set('theme', theme.value);
  }

  function setScrollLock(lock: boolean) {
    scrollDisabled.value = lock;
  }

  function addNavPage(page: NavPage) {
    if (!navPages.value.some((p) => p.route === page.route)) {
      navPages.value.push(page);
    }
  }

  function addFooterPage(page: NavPage) {
    if (!footerPages.value.some((p) => p.route === page.route)) {
      footerPages.value.push(page);
    }
  }

  function setNewsLetterPopupState(active: boolean) {
    newsLetterPopupActive.value = active;
  }

  return {
    // State
    mobile,
    theme,
    scrollDisabled,
    newsLetterPopupActive,
    navPages,
    footerPages,
    apps,
    social,
    // Actions
    setMobile,
    setTheme,
    setScrollLock,
    addNavPage,
    addFooterPage,
    setNewsLetterPopupState,
  };
});
